import { memo } from 'react';
import { useTranslation } from 'react-i18next';

import { Icons, IconType } from '../../../constants/icons';
import { SVGIconType } from '../../../constants/svgIcon';
import { Profile } from '../../../models/UserStore';
import { AvatarNode } from '../AvatarNode';
import { SvgIcon } from '../SVGIcon';

import './UserCell.css';

export enum UserCellSize {
  Large = 'large',
  Medium = 'medium',
  Small = 'small',
}

export enum UserCellVariant {
  Cyan = 'cyan',
  White = 'white',
  Transparent = 'transparent',
}

export type UserCellProps = {
  profile: Profile;
  purchasedAt?: Date;
  confirmReward?: string;
  confirmManageReward?: string;
  potentialReward?: string;
  referralCount?: number;
  customReferralText?: string;
  hasCreditIcon?: boolean;
  onClickReferral?: () => void;
};

export const UserCell = memo(
  ({
    profile,
    purchasedAt,
    confirmReward,
    confirmManageReward,
    potentialReward,
    referralCount,
    customReferralText,
    hasCreditIcon,
    onClickReferral,
  }: UserCellProps) => {
    const { t, i18n } = useTranslation();
    const options: Intl.DateTimeFormatOptions = {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    };
    const formattedDate = purchasedAt
      ? new Intl.DateTimeFormat(i18n.language, options).format(purchasedAt)
      : undefined;
    return (
      <div className="user-cell">
        <AvatarNode profile={profile} size={64} />
        <div className="text">
          <div className="name">
            <p>{profile.name}</p>
          </div>
          {formattedDate && (
            <div className="purchased">
              <span>{t('Purchased: ')}</span>
              <span>{formattedDate}</span>
            </div>
          )}
          {confirmReward ? (
            <div className="reward">
              <span>{t('Confirmed Reward: ')}</span>
              {hasCreditIcon && (
                <img
                  src={Icons[IconType.Credit]}
                  className={'small-cash-icon'}
                />
              )}
              <span className="confirmedValue">{confirmReward}</span>
            </div>
          ) : potentialReward ? (
            <div className="reward">
              <span>{t('Potential Reward: ')}</span>
              {hasCreditIcon && (
                <img
                  src={Icons[IconType.Credit]}
                  className={'small-cash-icon'}
                />
              )}
              <span className="potential-span">{potentialReward}</span>
            </div>
          ) : null}
          {confirmManageReward ? (
            <div className="reward">
              <span>{t('Confirmed Management Reward: ')}</span>
              {hasCreditIcon && (
                <img
                  src={Icons[IconType.Credit]}
                  className={'small-cash-icon'}
                />
              )}
              <span className="confirmedValue">{confirmManageReward}</span>
            </div>
          ) : null}
        </div>
        {onClickReferral ? (
          <div className="button-set">
            <button className="button" onClick={onClickReferral}>
              {customReferralText ? (
                t(customReferralText)
              ) : (
                <>
                  <span>{referralCount}</span> {t('Referred')}
                </>
              )}
              <SvgIcon name={SVGIconType.Arrow} className={'right-icon'} />
            </button>
          </div>
        ) : null}
      </div>
    );
  },
);
